import React from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, HelpCircle, Headphones, Newspaper, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'

export const ResourcesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <BookOpen className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Resources & Support
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Everything you need to succeed with your 1031 exchange. Find answers, learn best practices, and get the support you need.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {/* FAQ */}
            <Link 
              to="/faq" 
              className="group bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:border-[#69adba] dark:hover:border-[#69adba]"
            >
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 group-hover:bg-[#69adba]/20 dark:group-hover:bg-[#69adba]/30 transition-colors">
                <HelpCircle className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 group-hover:text-[#69adba] transition-colors">
                Frequently Asked Questions
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Find answers to common questions about 1031 exchanges and our platform.
              </p>
              <div className="flex items-center text-[#69adba] font-medium">
                <span>Learn More</span>
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Documentation */}
            <Link 
              to="/documentation" 
              className="group bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:border-[#69adba] dark:hover:border-[#69adba]"
            >
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 group-hover:bg-[#69adba]/20 dark:group-hover:bg-[#69adba]/30 transition-colors">
                <BookOpen className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 group-hover:text-[#69adba] transition-colors">
                Documentation
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Comprehensive guides, tutorials, and API documentation to help you get started.
              </p>
              <div className="flex items-center text-[#69adba] font-medium">
                <span>View Docs</span>
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Support */}
            <Link 
              to="/support" 
              className="group bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:border-[#69adba] dark:hover:border-[#69adba]"
            >
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 group-hover:bg-[#69adba]/20 dark:group-hover:bg-[#69adba]/30 transition-colors">
                <Headphones className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 group-hover:text-[#69adba] transition-colors">
                Support
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Get help from our support team via email, live chat, or phone.
              </p>
              <div className="flex items-center text-[#69adba] font-medium">
                <span>Get Support</span>
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Blog */}
            <Link 
              to="/blog" 
              className="group bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:border-[#69adba] dark:hover:border-[#69adba]"
            >
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 group-hover:bg-[#69adba]/20 dark:group-hover:bg-[#69adba]/30 transition-colors">
                <Newspaper className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 group-hover:text-[#69adba] transition-colors">
                Blog & Insights
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Stay informed with the latest news, tips, and insights about 1031 exchanges.
              </p>
              <div className="flex items-center text-[#69adba] font-medium">
                <span>Read Blog</span>
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

